import type { ReactElement } from 'react';

import { useIsLargeScreen } from '@bestseller-bit/frontend-community.theme.buying-theme';
import { useDefaultInputSize } from '@buying/library';
import Input from '@mui/material/Input';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { formatIdFromLabel } from './utils';

interface HorizontalReadOnlyFieldProps {
    label: string;
    value: string;
    disabled?: boolean;
    wrapperSx?: Record<string, unknown>;
}

const HorizontalReadOnlyField = ({
    label,
    value,
    disabled = false,
    wrapperSx,
}: HorizontalReadOnlyFieldProps): ReactElement => {
    const { palette } = useTheme();
    const isLargeScreen = useIsLargeScreen();
    const inputSize = useDefaultInputSize();

    return (
        <Stack
            direction="row"
            spacing={2}
            sx={{
                alignItems: 'center',
                minHeight: isLargeScreen ? '20px' : '16px',
                ...wrapperSx,
            }}
            useFlexGap
        >
            {/* Replicating form label */}
            <Typography
                variant={isLargeScreen ? 'buttons.small' : 'buttons.xsmall'}
                sx={{
                    color: disabled
                        ? alpha(palette.primary.main, 0.2)
                        : alpha(palette.primary.main, 0.5),
                    // Small alignment fix for labels next to inputs on large screens.
                    ...(isLargeScreen && { paddingTop: '1px' }),
                }}
            >
                {label}
            </Typography>

            <Input
                id={formatIdFromLabel(label)}
                value={value}
                size={inputSize}
                disabled={disabled}
                sx={{
                    flexGrow: 1,
                    padding: '0 !important',
                }}
                readOnly
            />
        </Stack>
    );
};

export { HorizontalReadOnlyField };
